import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Heart } from "lucide-react";
import ToolPageLayout from "@/components/tools/ToolPageLayout";
import { getRelatedTools } from "@/lib/toolsData";

const CLVCalculator = () => {
  const [avgPurchase, setAvgPurchase] = useState<string>("");
  const [purchaseFreq, setPurchaseFreq] = useState<string>("");
  const [lifespan, setLifespan] = useState<string>("");
  const [result, setResult] = useState<number | null>(null);

  const calculate = () => {
    const avgNum = parseFloat(avgPurchase) || 0;
    const freqNum = parseFloat(purchaseFreq) || 0;
    const lifespanNum = parseFloat(lifespan) || 0;
    
    if (avgNum === 0 || freqNum === 0 || lifespanNum === 0) {
      setResult(null);
      return;
    }

    const clv = avgNum * freqNum * lifespanNum;
    setResult(clv);
  };

  const reset = () => {
    setAvgPurchase("");
    setPurchaseFreq("");
    setLifespan("");
    setResult(null);
  };

  const relatedTools = getRelatedTools("clv-calculator");

  return (
    <ToolPageLayout
      title="Customer Lifetime Value Calculator"
      description="Calculate CLV to understand the total revenue a customer generates over their relationship with your business."
      metaDescription="Customer Lifetime Value (CLV/LTV) predicts the total revenue a customer will generate. It's essential for determining how much to invest in customer acquisition and retention."
      icon={Heart}
      slug="clv-calculator"
      formula={{
        equation: "CLV = Average Purchase Value × Purchase Frequency × Customer Lifespan",
        variables: [
          { name: "Average Purchase Value", description: "Average amount spent per transaction" },
          { name: "Purchase Frequency", description: "Average number of purchases per year" },
          { name: "Customer Lifespan", description: "Average years a customer stays active" },
        ]
      }}
      example={{
        inputs: [
          { label: "Average Purchase", value: "$75" },
          { label: "Purchases per Year", value: "4" },
          { label: "Customer Lifespan", value: "3 years" },
        ],
        output: "$900 CLV",
        interpretation: "Each customer is worth $900 over their lifetime. Your CPA should be well under this for profitability."
      }}
      improvements={[
        "Increase average order value with upsells and bundles",
        "Drive repeat purchases with email marketing and loyalty programs",
        "Reduce churn to extend customer lifespan",
        "Improve product quality and customer experience",
        "Personalize recommendations to increase purchase frequency",
        "Segment customers to identify and nurture high-value segments",
      ]}
      relatedTools={relatedTools.map(t => ({ title: t.title, href: t.href, description: t.description }))}
      relatedService={{ title: "Email Marketing", href: "/email-marketing" }}
    >
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>Calculate CLV</CardTitle>
          <CardDescription>Enter your customer metrics</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="avgPurchase">Average Purchase Value ($)</Label>
            <Input
              id="avgPurchase"
              type="number"
              placeholder="75"
              value={avgPurchase}
              onChange={(e) => setAvgPurchase(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="purchaseFreq">Purchases per Year</Label>
            <Input
              id="purchaseFreq"
              type="number"
              placeholder="4"
              value={purchaseFreq}
              onChange={(e) => setPurchaseFreq(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="lifespan">Customer Lifespan (years)</Label>
            <Input
              id="lifespan"
              type="number"
              placeholder="3"
              value={lifespan}
              onChange={(e) => setLifespan(e.target.value)}
            />
          </div>

          <div className="flex gap-3">
            <Button onClick={calculate} className="flex-1">
              Calculate CLV
            </Button>
            <Button variant="outline" onClick={reset}>
              Reset
            </Button>
          </div>

          {result !== null && (
            <div className="mt-6 p-6 bg-primary/5 rounded-lg border border-primary/20">
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1">Customer Lifetime Value</p>
                <p className="text-4xl font-bold text-primary">
                  ${result.toLocaleString()}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </ToolPageLayout>
  );
};

export default CLVCalculator;
